import React, { useState, useRef, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { useSelector, useDispatch } from 'react-redux';
import { setStartDate } from '../../../../Reducer/SearchReducer';
import Icon from '../../../../Assets/Icon/CustomIcon';

dayjs.extend(isBetween);

const SLIDE_COUNT = 51;
const SLIDE_CENTER = 25;
const RECENTER_THRESHOLD = 10;

export default function WeeklyCalendar({ onDateSelect, refreshClick }) {
  const dispatch = useDispatch();
  const swiperRef = useRef(null);
  const pickerRef = useRef(null);
  const isRecentering = useRef(false);

  const startDateStr = useSelector((state) => state.searchInfo.startDate);
  const calendarDots = useSelector((state) => state.permit.calendarDots);
  const startDate = dayjs(startDateStr);

  const initialDate = startDate.isValid() ? startDate : dayjs();

  const [isWeekly, setIsWeekly] = useState(true);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const [centerDate, setCenterDate] = useState(initialDate);
  const [currentViewDate, setCurrentViewDate] = useState(initialDate);

  const displayMonth = currentViewDate.format('YYYY년 M월');
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setIsPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (
      isRecentering.current &&
      swiperRef.current &&
      !swiperRef.current.destroyed
    ) {
      swiperRef.current.slideTo(SLIDE_CENTER, 0);
      isRecentering.current = false;
    }
  }, [centerDate]);

  const getWeekDateAt = (index) =>
    centerDate.add(index - SLIDE_CENTER, 'week').startOf('week');

  const getMonthDateAt = (index) =>
    centerDate.add(index - SLIDE_CENTER, 'month').startOf('month');

  const getMonthDays = (baseDate) => {
    const startDay = baseDate.startOf('month').startOf('week');
    const endDay = baseDate.endOf('month').endOf('week');
    const days = [];
    let curr = startDay;
    while (curr.isBefore(endDay) || curr.isSame(endDay, 'day')) {
      days.push(curr);
      curr = curr.add(1, 'day');
    }
    return days;
  };

  const handleDateClick = useCallback(
    (date) => {
      dispatch(setStartDate(date.format('YYYY-MM-DD')));
      if (onDateSelect) onDateSelect(date);
      setCurrentViewDate(date);
    },
    [dispatch, onDateSelect]
  );

  const handleWeeklySlideChange = (swiper) => {
    if (isRecentering.current) return;

    const offset = swiper.activeIndex - SLIDE_CENTER;
    const date = centerDate.add(offset, 'week').startOf('week');
    setCurrentViewDate(date);

    if (Math.abs(offset) >= RECENTER_THRESHOLD) {
      isRecentering.current = true;
      setCenterDate(date);
    }
  };

  const handleMonthlySlideChange = (swiper) => {
    if (isRecentering.current) return;

    const offset = swiper.activeIndex - SLIDE_CENTER;
    const date = centerDate.add(offset, 'month').startOf('month');
    setCurrentViewDate(date);

    if (swiper && !swiper.destroyed) swiper.updateAutoHeight(300);

    if (Math.abs(offset) >= RECENTER_THRESHOLD) {
      isRecentering.current = true;
      setCenterDate(date);
    }
  };

  const handlePickerSelect = (targetDate) => {
    isRecentering.current = true;
    setCenterDate(
      isWeekly ? targetDate.startOf('week') : targetDate.startOf('month')
    );
    handleDateClick(targetDate);
    setIsPickerOpen(false);
  };

  const handleModeSwitch = (weekly) => {
    isRecentering.current = true;
    setCenterDate(
      weekly
        ? currentViewDate.startOf('week')
        : currentViewDate.startOf('month')
    );
    setIsWeekly(weekly);
  };

  const getDotStatus = (targetDate) => {
    if (!calendarDots || calendarDots.length === 0) return null;

    const matches = calendarDots.filter((item) =>
      targetDate.isBetween(
        dayjs(item.workStartDate).startOf('day'),
        dayjs(item.workEndDate).endOf('day'),
        null,
        '[]'
      )
    );

    if (matches.length === 0) return null;

    if (matches.some((item) => item.gubun.includes('반려'))) return '반려';
    if (matches.some((item) => item.gubun.includes('요청'))) return '요청';
    return '일반';
  };

  return (
    <div className="mb-1">
      <div className="mb-1 flex items-center justify-between px-1">
        <div className="flex items-center gap-3">
          <div className="relative" ref={pickerRef}>
            <button
              onClick={() => setIsPickerOpen(!isPickerOpen)}
              className="group flex items-center gap-1.5 rounded-md px-1 py-0.5 transition-colors hover:bg-slate-50"
            >
              <h2 className="text-sm font-bold text-slate-800">
                {displayMonth}
              </h2>
              <span
                className={`text-[8px] text-slate-400 transition-transform duration-200 ${isPickerOpen ? 'rotate-180' : ''}`}
              >
                ▼
              </span>
            </button>

            {isPickerOpen && (
              <div className="z-bottom-nav absolute top-8 left-0 w-60 rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
                <MonthYearPicker
                  currentDate={currentViewDate}
                  onSelect={handlePickerSelect}
                />
              </div>
            )}
          </div>

          <div className="relative flex h-6 w-20 items-center rounded-sm bg-slate-200 p-0.5">
            <div
              className={`absolute h-5 w-[calc(50%-2px)] rounded-sm bg-white shadow-sm transition-transform duration-200 ${isWeekly ? 'translate-x-0' : 'translate-x-full'}`}
            />
            <button
              onClick={() => handleModeSwitch(true)}
              className={`text-2xs relative z-10 flex flex-1 justify-center font-bold ${isWeekly ? 'text-slate-800' : 'text-slate-50'}`}
            >
              주
            </button>
            <button
              onClick={() => handleModeSwitch(false)}
              className={`text-2xsfont-bold relative z-10 flex flex-1 justify-center ${!isWeekly ? 'text-slate-800' : 'text-slate-500'}`}
            >
              월
            </button>
          </div>
        </div>

        <button
          onClick={refreshClick}
          className="p-1 text-slate-500 active:opacity-50"
        >
          <Icon.Refresh />
        </button>
      </div>

      {isWeekly ? (
        <Swiper
          key="weekly-swiper"
          slidesPerView={1}
          initialSlide={SLIDE_CENTER}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={handleWeeklySlideChange}
          className="w-full"
        >
          {[...Array(SLIDE_COUNT)].map((_, index) => {
            const startOfWeek = getWeekDateAt(index);
            const days = Array.from({ length: 7 }, (_, i) =>
              startOfWeek.add(i, 'day')
            );
            return (
              <SwiperSlide key={index}>
                <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-2">
                  {days.map((date, i) => (
                    <DateItem
                      key={date.format('YYYY-MM-DD')}
                      date={date}
                      i={i}
                      startDate={startDate}
                      onDateClick={handleDateClick}
                      dotStatus={getDotStatus(date)}
                      showDayName={true}
                    />
                  ))}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      ) : (
        <Swiper
          key="monthly-swiper"
          autoHeight={true}
          slidesPerView={1}
          initialSlide={SLIDE_CENTER}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={handleMonthlySlideChange}
          className="w-full"
        >
          {[...Array(SLIDE_COUNT)].map((_, index) => {
            const baseMonth = getMonthDateAt(index);
            const days = getMonthDays(baseMonth);
            return (
              <SwiperSlide key={index}>
                <div className="rounded-xl border border-slate-100 bg-white p-2">
                  <div className="mb-1 grid grid-cols-7">
                    {dayNames.map((day, i) => (
                      <span
                        key={i}
                        className={`py-1.5 text-center text-[11px] font-medium ${i === 0 ? 'text-red-500' : i === 6 ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {day}
                      </span>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-y-1">
                    {days.map((date) => (
                      <DateItem
                        key={date.format('YYYY-MM-DD')}
                        date={date}
                        i={date.day()}
                        startDate={startDate}
                        onDateClick={handleDateClick}
                        dotStatus={getDotStatus(date)}
                        isCurrentMonth={date.month() === baseMonth.month()}
                        showDayName={false}
                      />
                    ))}
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}
    </div>
  );
}

function DateItem({
  date,
  i,
  startDate,
  onDateClick,
  isCurrentMonth = true,
  showDayName,
  dotStatus,
}) {
  const isSelected = date.isSame(startDate, 'day');
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

  const getDotColor = () => {
    if (!dotStatus) return null;
    if (dotStatus.includes('반려')) return 'bg-red-400';
    if (dotStatus.includes('요청')) return 'bg-blue-400';
    return 'bg-slate-300';
  };

  const dotColorClass = getDotColor();

  return (
    <div
      onClick={() => onDateClick(date)}
      className={`relative mx-auto flex w-9 cursor-pointer flex-col items-center gap-1 rounded-lg transition-all ${showDayName ? 'py-1.5' : 'py-2.5'} ${isSelected ? 'border border-blue-100 bg-blue-50' : 'border border-transparent hover:bg-slate-50'} ${!isCurrentMonth ? 'opacity-20' : ''}`}
    >
      {showDayName && (
        <span
          className={`text-[11px] font-medium ${isSelected ? 'text-blue-600' : i === 0 ? 'text-red-400' : i === 6 ? 'text-slate-400' : 'text-slate-500'}`}
        >
          {dayNames[i]}
        </span>
      )}
      <span
        className={`text-sm font-bold ${isSelected ? 'text-blue-600' : i === 6 ? 'text-slate-400' : 'text-slate-700'}`}
      >
        {date.date()}
      </span>

      <div className="mt-0.5 flex h-1.5 items-center justify-center">
        {dotColorClass && (
          <div className={`h-1.5 w-1.5 rounded-full ${dotColorClass}`} />
        )}
      </div>
    </div>
  );
}

function MonthYearPicker({ currentDate, onSelect }) {
  const [viewDate, setViewDate] = useState(currentDate);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setViewDate(viewDate.subtract(1, 'year'));
          }}
          className="rounded px-2 py-1 text-xs font-bold hover:bg-slate-100"
        >
          ◀
        </button>
        <span className="text-[13px] font-bold text-slate-700">
          {viewDate.format('YYYY년')}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setViewDate(viewDate.add(1, 'year'));
          }}
          className="rounded px-2 py-1 text-xs font-bold hover:bg-slate-100"
        >
          ▶
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {months.map((m) => {
          const isSelected =
            viewDate.year() === currentDate.year() &&
            m - 1 === currentDate.month();
          return (
            <button
              key={m}
              type="button"
              onClick={() => onSelect(viewDate.month(m - 1))}
              className={`rounded-md py-2 text-[11px] font-medium transition-all ${isSelected ? 'bg-blue-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              {m}월
            </button>
          );
        })}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelect(dayjs());
        }}
        className="mt-1 border-t border-slate-50 pt-2 text-[11px] font-medium text-blue-600"
      >
        오늘로 이동
      </button>
    </div>
  );
}
